import { P2Alert, P2TextDescription } from 'react-bootstrap-common-script';
import StageE2PaymentSegment from './stage-e2-payment-segment';
import { SectionCard } from '../../../components/card';
import { Stepper } from '../../../components/stepper';

import { R2StageEStepperItems } from '../../../utils/shared-dropdown';
import { useAppSelector } from '../../../redux/hook';
import { useGetEndorseFormsQuery } from '../../../redux/services/getStageE';
import React from 'react';
import PhysicalFormAccordion from './physical-form-accordion';
import SignifyFormAccordion from './signify-form-accordion';
import { useFormUpdater } from '../../../hooks/useFormUpdater';
import { StageDEndorseFormInfo } from '../../../models/classes/stage-d/Review-Application.class';

interface StageE2ViewProps {
  existingRent: boolean;
  isProcessingE2: boolean;
}
const StageE2View = ({ existingRent, isProcessingE2 }: StageE2ViewProps) => {
  // Redux dispatch and selectors
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);

  // Extract query parameter (applicationId) from the URL
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationId = queryParameters.get('application_id');

  // API call: fetch completion day endorse forms using query parameters and auth data.
  const {
    data: completionDayData,
    refetch: refetchEndorseForms,
    isLoading,
    isUninitialized,
    isSuccess: formFetchSuccess,
  } = useGetEndorseFormsQuery({
    application_id: applicationId ?? '0',
    uinfin: loginUser?.uinfin ?? '',
    singpass_token: singpassToken ?? '',
  });
  // Use the form updater hook with StageDEndorseFormInfo type
  useFormUpdater<StageDEndorseFormInfo>({
    loginUser: loginUser?.uinfin ?? '',
    singpassToken: singpassToken ?? '',
    applicationUuid: applicationId ?? '',
    isUninitialized: isUninitialized,
    data: completionDayData?.endorse_forms,
    onRefetch: refetchEndorseForms,
  });

  
  // useRefetchOnRefocus(formFetchSuccess ? refetchEndorseForms : undefined);

  // // Memoized helper function to trigger a backend update via a proxy for a given signify document.
  // // This function calls Proxy to call Signify third-party API and returns a boolean indicating whether the update was successful.
  // const triggerBackendUpdateViaProxy = useCallback(
  //   async (formId: number): Promise<boolean> => {
  //     try {
  //       const response = await PolymerAxios(loginUser?.uinfin ?? '', singpassToken).post(`${API_EndorseForm}?formId=${formId}`, {
  //         applicationUuid: applicationId,
  //       });

  //       // Check for a successful response.
  //       if (response.status === 200) {
  //         return true;
  //       } else {
  //         console.error(`Unexpected response status for signifyDocumentId ${formId}: ${response.status}`);
  //         return false;
  //       }
  //     } catch (error) {
  //       console.error(`Error updating backend for signifyDocumentId ${formId}:`, error);
  //       return false;
  //     }
  //   },
  //   [loginUser?.uinfin, singpassToken]
  // );

  // Process all forms (sorting, computing last signed date, filtering owners)
  const processedForms = React.useMemo(() => {
    if (!completionDayData?.endorse_forms) return [];

    return (
      completionDayData.endorse_forms
        // Sort alphabetically by title.
        .slice() // Create a shallow copy to avoid mutating original data.
        .sort((a: { title: string }, b: { title: any }) => a.title.localeCompare(b.title))
        .map((form: { owners: any[] }) => {
          // Calculate the latest endorsement date if owners exist.
          const lastSignedDate =
            form.owners && form.owners.length > 0
              ? form.owners.reduce((latest, owner) => {
                  const currentDate = new Date(owner.endorsement_date?.replace(' ', 'T'));
                  const latestDate = latest ? new Date(latest.replace(' ', 'T')) : new Date(0);
                  return currentDate > latestDate ? owner.endorsement_date : latest;
                }, null)
              : null;

          return {
            ...form,
            lastSignedDate,
          };
        })
        .map((form: { owners: any[] }) => ({
          ...form,
          // Filter owners to include only the current user.
          owners: form.owners.filter((owner) => owner.uinfin === loginUser?.uinfin),
        }))
    );
  }, [completionDayData?.endorse_forms, loginUser?.uinfin]);

  // Forms that do NOT require signing.
  const formWithoutSignify = React.useMemo(
    () => processedForms.filter((form: { signing_require: boolean }) => form.signing_require === false),
    [processedForms]
  );

  // Forms that require signing (assuming signing_require is true) and have at least one owner.
  const formswithSignify = React.useMemo(
    () =>
      processedForms.filter((form: { signing_require: boolean; owners: string | any[] }) => form.signing_require === true && form.owners.length > 0),
    [processedForms]
  );

  /**
   * Effect to trigger backend updates via a proxy for each endorse form.
   *
   * For each endorse form, it calls the triggerBackendUpdateViaProxy function.
   * Once all proxy requests are processed (regardless of success or failure), it refetches the endorse forms data from .
   */
  // useEffect(() => {
  //   if (isLoading || !formFetchSuccess) return;

  //   // Process only the forms that are not signed.
  //   const fetchPromises = formswithSignify.map((form: { signify_status: string; id: number }) => {
  //     if (form.signify_status.toLowerCase() !== 'signed') {
  //       // Wrap the API call in a function so it can be retried.
  //       return retry(() => triggerBackendUpdateViaProxy(form.id), 3, 1000);
  //     }
  //     // If signed, simply resolve.
  //     return Promise.resolve();
  //   });

  //   // Wait for all update calls (including retries) to settle, then refetch data.
  //   Promise.allSettled(fetchPromises).then(() => {
  //     refetchEndorseForms();
  //   });
  // }, [isLoading, formFetchSuccess, refetchEndorseForms, formswithSignify]);

  return (
    <>
      <div>
        <Stepper activeStep={2} items={R2StageEStepperItems} />
      </div>
      <div>
        <div className='mx-auto mb-3' style={{ maxWidth: '824px' }}>
          {isProcessingE2 && (
            <div>
              <P2Alert
                variant='info'
                withBorder={true}
                title='HDB is updating the ownership records and finalising payments'
                text='You will be notified if HDB requires further information and clarification from any owners.'
              />
            </div>
          )}
        </div>
        {existingRent && (
          <div className='rofs-alert'>
            <P2Alert
              variant={'warning'}
              text={
                'The existing renting out application for this flat will be terminated upon the completion of the change in ownership. New owners will need to submit a new application for renting out, subject to prevailing eligibility conditions.'
              }
            />
          </div>
        )}

        <div className='d-flex justify-content-center'>
          <SectionCard
            required={false}
            className='flex-fill'
            activeStep={2}
            stepperItems={[R2StageEStepperItems[1]]}
            titleChildren={<P2TextDescription title={`During Completion Appointment`} />}
            contentChildren={
              <div className='polymer-stage-e'>
                <div className='polymer-stage-e-reminder px-4 pb-4'>
                  <P2TextDescription
                    color='#333'
                    title={'Items to do'}
                    description={'Please take note of these items and details below to prepare for your completion appointment.'}
                  />
                </div>
                <StageE2PaymentSegment />
                <PhysicalFormAccordion forms={formWithoutSignify} />
                <SignifyFormAccordion forms={formswithSignify} />
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};
export default StageE2View;
